import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Text, useMainContext } from "../../../../../modules/Dashboard";
import { usePredictContext } from "../../../context/PredictContextProvider";
import { useGetAssistantCategories } from "../../../services/usePredict";
import PairImg from "../../PairImg";
import SingleStarIcon from "../../../../../components/icons/SingleStarIcon";
import RocketIcon from "../../../../../components/icons/RocketIcon";
import { ReactComponent as StarVector } from '../../../assets/analyze-type-bg.svg'
import { ScaleLoader } from "react-spinners";

// NEW VERSION — ONLY ONE CATEGORY & ONE ASSISTANT SHOWN AT A TIME
// Scroll vertically to switch assistants (no scrollbar)
// A large Launch button under each assistant


// Smooth scroll step distance
const SCROLL_THRESHOLD = 20;

const   RCategorizedAssistant = ({ allAssistants }) => {
    const navigate = useNavigate();
    const { assistant, step, setTicker, setTimeFrame, setResponseType } =
        usePredictContext();

    const { data: categories, isLoading } = useGetAssistantCategories();

    // Flatten all assistants across categories → ONE LIST
    const assistantList = categories
        ? categories.flatMap((cat) => cat.list)
        : [];

    const [activeIndex, setActiveIndex] = useState(0);
    const scrollRef = useRef(null);
    const lastScrollY = useRef(0);

    const handleLaunch = (item) => {
        const assistantItem = allAssistants.find((a) => a?.value === item.assistant);
        if (!assistantItem) return;

        assistant.setAssistant(assistantItem);
        setTimeFrame(item?.frame || "");
        setResponseType("complete");
        setTicker(item.ticker.toUpperCase());
        step.setCurrentStep("result");
        navigate(`/assistant/${assistantItem.tickerCase}?step=result`);
    };

    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;


        let touchStartY = 0;
        let touchEndY = 0;


        const handleScroll = () => {
            const delta = container.scrollTop - lastScrollY.current;


            if (delta > SCROLL_THRESHOLD) {
                setActiveIndex((i) => Math.min(i + 1, assistantList.length - 1));
                lastScrollY.current = container.scrollTop;
            } else if (delta < -SCROLL_THRESHOLD) {
                setActiveIndex((i) => Math.max(i - 1, 0));
                lastScrollY.current = container.scrollTop;
            }
        };


        const handleTouchStart = (e) => {
            touchStartY = e.touches[0].clientY;
        };


        const handleTouchMove = (e) => {
            touchEndY = e.touches[0].clientY;
        };


        const handleTouchEnd = () => {
            const delta = touchStartY - touchEndY;


            if (delta > 50) {
                // swipe up
                setActiveIndex((i) => Math.min(i + 1, assistantList.length - 1));
            } else if (delta < -50) {
                // swipe down
                setActiveIndex((i) => Math.max(i - 1, 0));
            }
        };


        container.addEventListener("scroll", handleScroll);
        container.addEventListener("touchstart", handleTouchStart);
        container.addEventListener("touchmove", handleTouchMove);
        container.addEventListener("touchend", handleTouchEnd);


        return () => {
            container.removeEventListener("scroll", handleScroll);
            container.removeEventListener("touchstart", handleTouchStart);
            container.removeEventListener("touchmove", handleTouchMove);
            container.removeEventListener("touchend", handleTouchEnd);
        };
    }, [assistantList]);

    // Snap scroll to active assistant
    useEffect(() => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollTo({
            top: activeIndex * window.innerHeight * 0.9,
            behavior: "smooth",
        });
    }, [activeIndex]);

    if (isLoading) return <LoadingScreen />;

    const item = assistantList[activeIndex];
    if (!item) return null;

    return (
        <div className='bg-gradient-to-br from-[#101828] via-[#3C0366] to-[#101828] rounded-t-[40px] px-5 py-10'>
            {/* TITLE */}
            <div className='text-center w-full mb-5'>
                <Text tid={"best-week-tickers"} className='text-xl' />
            </div>
            <div
                ref={scrollRef}
                className="overflow-auto no-scrollbar snap-y snap-mandatory"
            >
                <div className=" flex flex-col items-center justify-center snap-center px-2">
                    {/* Assistant Card */}
                    <div className="bg-[#1E1533] w-full max-w-md px-7 py-4 rounded-2xl border border-purpleBorderDark mb-6 relative overflow-hidden">
                        <StarVector className="absolute top-0 left-0 z-[1] " />
                        <div className="flex items-center gap-3 mb-4 relative z-[2]">
                            <PairImg ticker={item.ticker} />
                            <span className="text-white">
                                <p className="text-lg">{item.ticker}</p>
                                <p className="uppercase text-xs text-[#99A1AF]">{item.ticker}</p>
                            </span>
                        </div>

                        <span className="bg-gradient-to-r from-[#F0B100] to-[#FF6900] flex gap-1 items-center px-3 py-1 rounded-lg max-w-max shadow-sm relative z-[2] ml-auto">
                            <SingleStarIcon size={14} color="#FFFFFF" />
                            <Text tid={item.assistant} className="text-sm" />
                        </span>
                    </div>

                    {/* BIG LAUNCH BUTTON */}
                    <button
                        onClick={() => handleLaunch(item)}
                        className="w-full max-w-md bg-gradient-to-r from-[#A41AE0] to-[#FB6608] py-4 rounded-2xl flex items-center justify-center gap-3 active:scale-95 transition-all shadow-lg mt-4"
                    >
                        <RocketIcon color="#FFFFFF" />
                        <Text tid="lunch-analysis" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RCategorizedAssistant;

const LoadingScreen = () => (
    <div className='max-w-screen-xl h-[calc(100vh-10rem)] flex items-center justify-center mx-auto backdrop-blur-2xl z-[999]'>
        <ScaleLoader color='#9F64DF' />
    </div>
);
